import { useEffect, useMemo, useState } from 'react'
import { sortBy } from 'lodash'
import moment from 'moment'

import {
  Card,
  Select,
  Input,
  Badge,
  Link,
  Table,
  Text,
  PaginationPages,
} from '@/ui'
import { TypographyStyles } from '@/styles'

import { PayrollProfile } from '../payrollProfile.slice'
import { fetchAllUsersIfNeeded } from '@/actions/admin/adminAllUsersActions'
import { fetchPayrollUsers } from '../payrollActions'
import { useReselector } from '@/utils/sharedHooks'
import { getAllUsersById, getUserById } from '@/selectors/user.selectors'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useTableSort } from '@/utils/sortHelpers'
import { PayrollForecast } from './PayrollForecast'
import { useAppDispatch } from '@/utils/typeHelpers'

import { getStripeCustomerURL } from '@/features/admin/user-record/AccountDetailsTabPanel'

const PayrollRow = ({ profile }: { profile: PayrollProfile }) => {
  const user = useReselector(getUserById, profile.userId)

  const statusLabel = useMemo(() => {
    if (profile.onboardingCompleted) {
      return <Badge color="green">Completed</Badge>
    } else if (profile.needsMigration) {
      return <Badge color="red">Needs Migration</Badge>
    } else {
      return <Badge color="red">Onboarding</Badge>
    }
  }, [profile.needsMigration, profile.onboardingCompleted])

  if (!user) {
    return null
  }

  const joinedText = profile.onboardingCompleted
    ? profile.onboardedAt
      ? moment.utc(profile.onboardedAt).format(DATE_FORMATS.DISPLAY_SHORT)
      : 'Unknown'
    : 'Not Onboarded'

  const payrollType =
    profile.migratedFromGusto === true
      ? 'Migration'
      : profile.migratedFromGusto === false
        ? 'Net new'
        : 'Unknown'

  return (
    <Table.Row>
      <Table.Cell>
        <Text>
          <b>ID:</b>
          {'  '}
          <Link href={`/admin/finances/records/${user.id}`}>
            {user.id} {user.firstName} {user.lastName}
          </Link>
        </Text>
        <Text>
          <b>Gusto UUID:</b>
          {'  '}
          {profile.gustoCompanyUuid}
        </Text>
        <Text>
          <b>Business Name:</b>
          {'  '}
          {user.financialProfile?.businessName}
        </Text>
        {user.stripeCustomer && (
          <Link href={getStripeCustomerURL(user.stripeCustomer)} newPage>
            Stripe Customer Link
          </Link>
        )}
      </Table.Cell>
      <Table.Cell>
        <Text>
          <b>Date joined Heard Payroll:</b>
          {'  '}
          {joinedText}
        </Text>
        <Text>
          <b>Heard Payroll Type:</b>
          {'  '}
          {payrollType}
        </Text>
        <Text>
          <b>Number of Employees + Contractors Paid in Last Cycle:</b>
          {'  '}
          {profile.numberOfTeamMembers}
        </Text>
      </Table.Cell>
      <Table.Cell>{statusLabel}</Table.Cell>
    </Table.Row>
  )
}

enum SORT_BY {
  userId = 'userId',
  dateJoined = 'dateJoined',
  onboardingStatus = 'onboardingStatus',
}

enum FILTER_STATUS {
  completed = 'completed',
  onboarding = 'onboarding',
  needs_migration = 'useMigration',
}

export const PayrollUsers = () => {
  const dispatch = useAppDispatch()
  const usersById = useReselector(getAllUsersById)
  const [payrollProfiles, setPayrollProfiles] = useState<PayrollProfile[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [filteredStatus, setFilteredStatus] = useState<
    FILTER_STATUS | undefined
  >(undefined)

  const filteredProfiles = useMemo(() => {
    let newProfiles = [...payrollProfiles]
    if (filteredStatus === FILTER_STATUS.completed) {
      newProfiles = newProfiles.filter((profile) => profile.onboardingCompleted)
    } else if (filteredStatus === FILTER_STATUS.needs_migration) {
      newProfiles = newProfiles.filter((profile) => profile.needsMigration)
    } else if (filteredStatus === FILTER_STATUS.onboarding) {
      newProfiles = newProfiles.filter(
        (profile) => !profile.onboardingCompleted && !profile.needsMigration
      )
    }

    if (searchValue) {
      newProfiles = newProfiles.filter((profile) => {
        const user = usersById[profile.userId]
        return (
          user &&
          (`${user.firstName} ${user.lastName}`
            .toLowerCase()
            .includes(searchValue.toLowerCase()) ||
            user.financialProfile?.businessName
              ?.toLowerCase()
              .includes(searchValue.toLowerCase()))
        )
      })
    }

    return newProfiles
  }, [filteredStatus, payrollProfiles, searchValue, usersById])

  const { sortedValues, getHeaderCellPropsV2, paginationProps } = useTableSort({
    values: filteredProfiles,
    defaultSortBy: SORT_BY.userId,
    sortFunction: (profiles, sortByValue) => {
      if (sortByValue === SORT_BY.userId) {
        return sortBy(profiles, 'userId')
      } else if (sortByValue === SORT_BY.onboardingStatus) {
        return sortBy(profiles, (profile) => {
          if (profile.onboardingCompleted) {
            return 'completed'
          } else if (profile.needsMigration) {
            return 'needs migration'
          } else {
            return 'onboarding'
          }
        })
      } else {
        return sortBy(profiles, 'onboardedAt')
      }
    },
  })

  const onboardedUsersCount = useMemo(
    () => payrollProfiles.filter((pp) => pp.onboardingCompleted).length,
    [payrollProfiles]
  )

  const onboardingUsersCount = useMemo(
    () =>
      payrollProfiles.filter(
        (pp) => !pp.onboardingCompleted && pp.isActive && !pp.needsMigration
      ).length,
    [payrollProfiles]
  )

  const needsMigrationCount = useMemo(
    () =>
      payrollProfiles.filter(
        (pp) => !pp.onboardingCompleted && pp.isActive && pp.needsMigration
      ).length,
    [payrollProfiles]
  )

  useEffect(() => {
    const fetch = async () => {
      dispatch(fetchAllUsersIfNeeded(true, { includeFinancialProfile: true }))
      const res = await fetchPayrollUsers()(dispatch)

      if (res) {
        setPayrollProfiles(res)
      }
    }

    fetch()
  }, [dispatch])

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <Text variant={TypographyStyles.H2}>Heard Payroll Users</Text>

        <Text>
          <b>Total Payroll:</b> {payrollProfiles.length} Users
        </Text>

        <div className="flex flex-col gap-1">
          <Text>
            <b>Onboarded users:</b> {onboardedUsersCount} Users
          </Text>
          <Text>
            <b>Onboarding users:</b> {onboardingUsersCount} Users
          </Text>
          <Text>
            <b>Needs migration:</b> {needsMigrationCount} Users
          </Text>
        </div>

        <Text variant={TypographyStyles.H3}>
          {moment().year()} Heard Payroll Forecasting + Performance
        </Text>

        <PayrollForecast payrollProfiles={payrollProfiles} />

        <div className="flex flex-col lg:flex-row gap-4">
          <div className="lg:w-1/2">
            <Text variant={TypographyStyles.BODY_MD}>
              {' '}
              Find User or Business:
            </Text>
            <Input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Find User or Business"
            />
          </div>
          <div className="lg:w-1/2">
            <Text variant={TypographyStyles.BODY_MD}> Filter by Status:</Text>
            <Select
              value={filteredStatus}
              onValueChange={setFilteredStatus}
              placeholder="SelectStatus"
              options={[
                { text: 'Completed', value: FILTER_STATUS.completed },
                { text: 'Onboarding', value: FILTER_STATUS.onboarding },
                {
                  text: 'Needs Migration',
                  value: FILTER_STATUS.needs_migration,
                },
              ]}
            />
          </div>
        </div>

        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                {...getHeaderCellPropsV2(SORT_BY.userId)}
                sortable
              >
                User
              </Table.HeaderCell>
              <Table.HeaderCell
                {...getHeaderCellPropsV2(SORT_BY.dateJoined)}
                sortable
              >
                Payroll Details
              </Table.HeaderCell>
              <Table.HeaderCell
                {...getHeaderCellPropsV2(SORT_BY.onboardingStatus)}
                sortable
              >
                Payroll Onboarding Status
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          {sortedValues.map((profile) => (
            <PayrollRow key={profile.id} profile={profile} />
          ))}
        </Table>

        <div className="flex justify-center">
          <PaginationPages
            currentPage={paginationProps.currentPage}
            onPageClick={paginationProps.onPageClick}
            pages={paginationProps.pages}
          />
        </div>
      </div>
    </Card>
  )
}
